import React, { useEffect, useRef, useState } from 'react';
import { Card, CountUp, inr } from '../today/cards';
import type { DrawerActionType, UnitStep } from '../today/workflowBoard';
import type { LifecycleStage } from './lifecycle';
import type { ReceiptRow, UnitSummary } from './unitSummary';
import { usePaymentDetail } from '../../contexts/PaymentDetailContext';
import { UnitSwitcher, type SwitcherUnit } from './UnitSwitcher';

const PATHS: Record<string, React.ReactNode> = {
  left: <path d="M15 6l-6 6 6 6" />,
  back: <path d="M19 12H5m6-6-6 6 6 6" />,
  right: <path d="m9 6 6 6-6 6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="m5 12 5 5 9-10" />,
  alert: <><path d="M12 4 2.5 20h19z" /><path d="M12 10v4M12 17v.5" /></>,
  bank: <path d="M3 10h18L12 4zM5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />,
  phone: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />,
  file: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /></>,
  book: <><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" /><path d="M5 17a3 3 0 0 1 3-3h11" /></>,
  download: <><path d="M12 4v11" /><path d="m7 10 5 5 5-5" /><path d="M5 20h14" /></>,
  sliders: <><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></>,
  history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></>,
};
export const UIcon = ({ name, size = 16 }: { name: keyof typeof PATHS; size?: number }) => (
  <svg className="ot-ic" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{PATHS[name]}</svg>
);

export const short = (v: number) =>
  v >= 1e7 ? `₹${(v / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr` : v >= 1e5 ? `₹${(v / 1e5).toFixed(1).replace(/\.0$/, '')} L` : inr(v);
// Exact rupees, Indian grouping: ₹16,74,863.
export const rupees = (v: number) => `${v < 0 ? '-' : ''}₹${Math.round(Math.abs(v)).toLocaleString('en-IN')}`;
const dateLabel = (d: Date | null | undefined) => (d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const chip = (tone: string, text: React.ReactNode) => <span className={`ot-chip c-${tone}`}>{text}</span>;

/* ── header ── */
export function UnitHeader(props: {
  unitNumber: string;
  status: string;
  s: UnitSummary | null;
  sub: React.ReactNode;
  backLabel: string;
  onBack: () => void;
  prev: (() => void) | null;
  next: (() => void) | null;
  onRecord: (() => void) | null;
  onFullView: (() => void) | null;
  onClassic: () => void;
  onBookingForm?: (() => void) | null;
  bookingFormFile?: { url: string; name: string } | null;
  bookingFormDetails?: Array<[string, string]>;
  onCustomize?: (() => void) | null;
  switcher?: { units: SwitcherUnit[]; onPick: (id: string) => void; openSignal: number; onOpen?: () => void };
}) {
  const { s } = props;
  return (
    <header className="ou-head">
      <div className="ou-headmain">
        <div className="ou-title">
          <button className="ou-ibtn ou-back" onClick={props.onBack} aria-label={`Back to ${props.backLabel}`} title={`Back to ${props.backLabel}`}><UIcon name="back" /></button>
          {props.switcher ? <UnitSwitcher current={props.unitNumber} {...props.switcher} /> : <h1>{props.unitNumber}</h1>}
          {chip(props.status === 'Available' ? 'mute' : props.status === 'Hold' ? 'warn' : 'acc', props.status)}
          {s && s.overdue > 0 ? chip('bad', `${short(s.overdue)} overdue`) : null}
        </div>
      </div>
      <div className="ou-actions">
        {props.onRecord ? <button className="ot-btn dark ou-desk" onClick={props.onRecord} title="Record receipt (R)"><UIcon name="plus" /> Record receipt</button> : null}
        <button className="ou-ibtn ou-desk" onClick={props.onClassic} aria-label="Classic view" title="Classic view"><UIcon name="history" /></button>
        {props.onBookingForm ? (
          <span className="ou-tipwrap">
            <button className="ou-ibtn" onClick={props.onBookingForm} aria-label="Booking form" aria-describedby={props.bookingFormDetails?.length ? 'ou-bf-tip' : undefined} title={props.bookingFormDetails?.length ? undefined : 'Booking form'}><UIcon name="file" /></button>
            {props.bookingFormDetails?.length ? (
              <span className="ou-tip" role="tooltip" id="ou-bf-tip">
                <b>Booking form details</b>
                {props.bookingFormDetails.map(([k, v]) => <span key={k} className="ou-tiprow"><span>{k}</span><span>{v}</span></span>)}
              </span>
            ) : null}
          </span>
        ) : null}
        {props.bookingFormFile ? <a className="ou-ibtn" href={props.bookingFormFile.url} target="_blank" rel="noopener noreferrer" download={props.bookingFormFile.name} aria-label="Download booking form" title="Download booking form"><UIcon name="download" /></a> : null}
        {props.onCustomize ? <button className="ou-ibtn ou-custbtn" onClick={props.onCustomize} aria-label="Customise page" title="Customise page"><UIcon name="sliders" /></button> : null}
        {props.onFullView ? <button className="ou-ibtn" onClick={props.onFullView} aria-label="Full booking view" title="Full booking view"><UIcon name="book" /></button> : null}
        <div style={{ display: 'flex', gap: 6, marginLeft: 6 }}>
          <button className="ou-ibtn" onClick={props.prev || undefined} disabled={!props.prev} aria-label="Previous unit" title="Previous unit (J)"><UIcon name="left" /></button>
          <button className="ou-ibtn" onClick={props.next || undefined} disabled={!props.next} aria-label="Next unit" title="Next unit (K)"><UIcon name="right" /></button>
        </div>
      </div>
      <div className="ot-sub ou-headsub">{props.sub}</div>
    </header>
  );
}

/* ── payment hero ── */
export function PaymentCard({ s }: { s: UnitSummary }) {
  const pct = (v: number) => (s.value > 0 ? Math.min(100, (v / s.value) * 100) : 0);
  // Who the balance is expected from. With a loan to builder, use the Collection snapshot split; otherwise
  // fall back to the sanction still to be released. Never more from the bank than the unit still owes
  // (a sanction can cover several units of the same buyer).
  const loanPart = s.collection.parts.find((p) => p.label === 'Loan to Builder');
  const fromBankRaw = loanPart ? loanPart.pending : s.loan.needed ? Math.max(0, s.loan.amount - s.loan.disbursed) : 0;
  const fromBank = Math.min(s.pending, Math.max(0, fromBankRaw));
  const fromBuyer = Math.max(0, s.pending - fromBank);
  return (
    <Card className="tint" index={0} label="Payment">
      <div className="ot-hd">
        <div><h3>Payment</h3></div>
        {s.overdue > 0 ? chip('bad', `Overdue since ${s.overdueSinceLabel}`) : s.received >= s.value && s.value > 0 ? chip('good', 'Fully paid') : null}
      </div>
      <div className="ot-lbl">Received</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, margin: '4px 0 14px' }}>
        <div className="ot-big"><CountUp value={s.received} format={rupees} /></div>
        <div className="muted">of <b className="num" style={{ color: 'var(--ot-ink)' }}>{rupees(s.value)}</b> · {s.paidPct}% paid</div>
      </div>
      <div className="ot-meter" style={{ height: 14 }} role="img" aria-label={`Received ${rupees(s.received)}${fromBank ? `, ${rupees(fromBank)} expected from the bank` : ''}${fromBuyer ? `, ${rupees(fromBuyer)} still to come from the buyer` : ''}`}>
        <i className="m-acc" style={{ width: `${pct(s.received)}%` }} />
        {fromBank > 0 ? <i className="m-hatch" style={{ width: `${pct(fromBank)}%` }} /> : null}
        {fromBuyer > 0 ? <i className="ou-m-buyer" style={{ width: `${pct(fromBuyer)}%` }} /> : null}
      </div>
      <div className="ot-legend" style={{ marginTop: 10 }}>
        <span><i className="m-acc" />Received <b className="num">{rupees(s.received)}</b></span>
        {fromBank > 0 ? <span><i className="m-hatch" />Loan pending <b className="num">{rupees(fromBank)}</b></span> : null}
        {fromBuyer > 0 ? <span><i className="ou-m-buyer" />Buyer to pay <b className="num">{rupees(fromBuyer)}</b>{s.overdue > 0 ? <span className="ou-late"> · {rupees(s.overdue)} overdue</span> : null}</span> : null}
      </div>
      <div className="ot-statrow" style={{ marginTop: 16 }}>
        <div>
          <div className="ot-lbl">Balance</div>
          <div className="ot-mid num">{rupees(s.pending)}</div>
          {s.pending > 0 ? <span className="faint" style={{ fontSize: 12 }}>{fromBank > 0 ? (fromBuyer > 0 ? 'Bank and buyer' : 'All from the bank loan') : 'All from the buyer'}</span> : null}
        </div>
        {s.hasPlan ? (
          <div>
            <div className="ot-lbl">Next due</div>
            <div className="ot-mid num">{s.nextDue ? rupees(s.nextDue.amount - s.nextDue.paid) : 'None'}</div>
            {s.nextDue ? <span className="faint" style={{ fontSize: 12 }}>{s.nextDue.label} · {s.nextDue.dueLabel.replace(/^By /, '')}</span> : null}
          </div>
        ) : null}
        <div>
          <div className="ot-lbl">Last receipt</div>
          <div className="ot-mid num">{s.lastReceipt ? rupees(s.lastReceipt.amount) : '-'}</div>
          {s.lastReceipt ? <span className="faint" style={{ fontSize: 12 }}>{dateLabel(new Date(s.lastReceipt.ms))}{s.lastReceipt.mode ? ` · ${s.lastReceipt.mode}` : ''}</span> : null}
        </div>
      </div>
    </Card>
  );
}

/* ── needs attention ── */
export function AttentionCard({ s }: { s: UnitSummary }) {
  return (
    <Card index={1} label="Needs attention">
      <div className="ot-hd"><div><h3 style={{ color: s.attention.length ? 'var(--ot-bad-ink)' : undefined }}>Needs attention</h3><p>What is holding this unit up</p></div></div>
      {s.attention.length ? (
        <div className="ot-list">
          {s.attention.map((a) => (
            <div className="ot-item" key={a.id} style={{ alignItems: 'flex-start' }}>
              <span className={`ot-ibox c-${a.tone}`}><UIcon name="alert" /></span>
              <span className="bd"><span className="t" style={{ whiteSpace: 'normal' }}>{a.title}</span><span className="s" style={{ display: 'block', whiteSpace: 'normal' }}>{a.detail}</span></span>
            </div>
          ))}
        </div>
      ) : (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>Nothing is holding this unit up. Payments, paperwork and KYC are on track.</p>
      )}
    </Card>
  );
}

/* ── pending work: every step that can be done now, Sales and Loan ── */
export function PendingWorkCard({ steps, onAction, onOpen }: { steps: UnitStep[]; onAction: (step: UnitStep) => void; onOpen: (which: 'main' | 'loan') => void }) {
  const ready = steps.filter((x) => x.status === 'ready');
  const groups = (['main', 'loan'] as const)
    .map((which) => ({ which, rows: ready.filter((x) => x.workflowType === which) }))
    .filter((g) => g.rows.length);
  return (
    <Card index={2} label="Pending work">
      <div className="ot-hd">
        <div><h3>Pending work</h3><p>{ready.length ? `${ready.length} step${ready.length === 1 ? '' : 's'} can be done now` : 'Nothing to do right now'}</p></div>
        {ready.length ? <span className="ot-chip c-acc num">{ready.length}</span> : null}
      </div>
      {groups.map((g) => (
        <div key={g.which} className="ou-pw-group">
          {groups.length > 1 ? <div className="ot-lbl ou-pw-lbl">{g.which === 'loan' ? 'Loan' : 'Sales'}</div> : null}
          <div className="ot-list">
            {g.rows.map((x) => (
              <div className="ot-item" key={`${x.workflowType}-${x.key}`}>
                <span className={`ot-ibox c-${x.workflowType === 'loan' ? 'acc' : 'warn'}`}><UIcon name={x.workflowType === 'loan' ? 'bank' : 'file'} /></span>
                <span className="bd"><span className="t" style={{ whiteSpace: 'normal' }}>{x.title}</span></span>
                <button className="ot-btn sm" onClick={() => (x.action ? onAction(x) : onOpen(x.workflowType))} title={x.action ? undefined : 'Opens this step in the booking drawer'}>
                  {x.action?.label || 'Open'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ── step bar ── */
export function StepBar({ steps, picked, onPick, onAction }: { steps: UnitStep[]; picked: number; onPick: (i: number) => void; onAction: (step: UnitStep) => void }) {
  const done = steps.filter((x) => x.status === 'done').length;
  const sel = steps[picked] || steps[0];
  const rail = useRef<HTMLDivElement>(null);
  // On narrow screens the steps scroll sideways: bring the picked step into the middle without moving the page.
  useEffect(() => {
    const el = rail.current;
    const node = el?.children[picked] as HTMLElement | undefined;
    if (!el || !node || el.scrollWidth <= el.clientWidth) return;
    el.scrollTo({ left: node.offsetLeft - el.clientWidth / 2 + node.offsetWidth / 2, behavior: 'auto' });
  }, [picked]);
  return (
    <Card className="panel" index={2} label="Booking to possession">
      <div className="ot-hd"><div><h3>Booking to possession</h3><p>{done} of {steps.length} steps done · tap a step to jump to its stage</p></div></div>
      <div className="ot-steps" ref={rail} style={{ ['--n' as any]: steps.length }}>
        {steps.map((st, i) => {
          const firstReady = steps.findIndex((x) => x.status === 'ready') === i;
          return (
            <button key={`${st.workflowType}:${st.key}`} className={`ot-step ${st.status === 'done' ? 'done' : ''} ${firstReady ? 'hot' : ''}`} aria-pressed={picked === i} onClick={() => onPick(i)}>
              <span className="node num">{st.status === 'done' ? <UIcon name="check" size={15} /> : i + 1}</span>
              <span className="lab">{st.title}</span>
            </button>
          );
        })}
      </div>
      {sel ? (
        <div className="ou-stepbox">
          <div style={{ minWidth: 0, flex: 1 }}>
            <b style={{ display: 'block' }}>{sel.title}{sel.workflowType === 'loan' ? ' (loan)' : ''}</b>
            <span className="muted" style={{ fontSize: 13 }}>{sel.status === 'done' ? 'Done.' : sel.status === 'ready' ? 'Ready to do now.' : 'Waits for an earlier step.'}</span>
          </div>
          {sel.status === 'ready' ? <button className="ot-btn sm ou-light" onClick={() => onAction(sel)}>{sel.action?.label || 'Open'}</button> : null}
        </div>
      ) : null}
    </Card>
  );
}

/* ── lifecycle ── */
const STAGE_NOTE: Record<string, (s: UnitSummary) => string> = {
  booking: (s) => `${s.dates.booking ? `Booked ${dateLabel(s.dates.booking)}` : `Booking form ${dateLabel(s.dates.form) || 'filled'}, no receipt yet`}${s.broker ? ` through ${s.broker}` : ''}. ${s.buyers.length} buyer${s.buyers.length === 1 ? '' : 's'} on the booking.`,
  dp: () => 'Down payment, GST on it and the allotment letter.',
  agreement: (s) => (s.dates.agreement ? `Agreement registered on ${dateLabel(s.dates.agreement)}.` : 'Sale agreement with the advocate: signing, registration and its charges.'),
  loan: (s) => (s.loan.sanctioned ? `${s.loan.bank || 'Bank'} sanctioned ${short(s.loan.amount)}${s.loan.sanctionDate ? ` on ${s.loan.sanctionDate}` : ''}. ${short(s.loan.disbursed)} disbursed so far.` : s.loan.started ? 'Loan file in progress with the bank.' : 'The buyer needs a loan; the loan file is not started.'),
  instalments: (s) => (s.hasPlan ? `${s.plan.filter((r) => r.state === 'paid').length} of ${s.plan.length} instalments paid.` : 'No structured payment plan on this booking.'),
  deed: (s) => (s.deedDone ? `Sale deed done${s.dates.deed ? ` on ${dateLabel(s.dates.deed)}` : ''}.` : 'Needs full payment and the building BUC.'),
  possession: () => 'Handover, fit-out access form and keys.',
};

export function PlanList({ s }: { s: UnitSummary }) {
  if (!s.hasPlan) return <p className="faint" style={{ margin: 0, fontSize: 13 }}>This booking has no structured payment plan, so due dates can't be shown. Add one in Full booking view.</p>;
  return (
    <div className="ou-plan">
      {s.plan.map((r, i) => (
        <div className="ou-prow" key={i}>
          <span className={`ou-dot ${r.state}`} />
          <span style={{ minWidth: 0 }}><b style={{ fontWeight: 500 }}>{r.label}</b><br /><span className="faint mono" style={{ fontSize: 12 }}>{r.dueLabel}</span></span>
          <span className="num" style={{ textAlign: 'right' }}><b style={{ fontWeight: 600 }}>{short(r.amount)}</b><br /><span className="faint" style={{ fontSize: 12 }}>{r.paid > 0 && r.paid < r.amount ? `${short(r.paid)} paid` : ''}</span></span>
          {r.state === 'paid' ? chip('good', 'Paid') : r.state === 'late' ? chip('bad', `${short(r.amount - r.paid)} due`) : r.state === 'part' ? chip('warn', 'Part paid') : r.state === 'later' ? chip('mute', 'On possession') : chip('mute', 'Upcoming')}
        </div>
      ))}
    </div>
  );
}

export function LifecycleCard({ s, picked, onAction, onRecord }: { s: UnitSummary; picked: string | null; onAction: (step: UnitStep) => void; onRecord: () => void }) {
  const nowIndex = s.lifecycle.findIndex((st) => st.state === 'now');
  return (
    <Card index={3} label="Lifecycle">
      <div className="ot-hd" style={{ marginBottom: 16 }}>
        <div><h3>Lifecycle</h3></div>
        {nowIndex >= 0 ? chip('acc', `Stage ${nowIndex + 1} of ${s.lifecycle.length}`) : chip('good', 'All stages done')}
      </div>
      <div className="ou-life ou-cap tall">
        {s.lifecycle.map((st: LifecycleStage, i) => {
          const open = st.state === 'now' || picked === st.key;
          return (
            <div key={st.key} id={`stage-${st.key}`} className={`ou-stage ${st.state} ${picked === st.key ? 'picked' : ''}`}>
              <span className="mark">{st.state === 'done' ? <UIcon name="check" size={13} /> : i + 1}</span>
              <div className="ou-stagecard">
                <div className="ot-hd" style={{ marginBottom: 0 }}>
                  <div><h3 style={{ fontSize: 15 }}>{st.title}</h3><p>{STAGE_NOTE[st.key](s)}</p></div>
                  {st.state === 'done' ? chip('good', 'Done') : st.state === 'now' ? chip('acc', 'Now') : chip('mute', 'Coming')}
                </div>
                {open && st.steps.length ? (
                  <div className="ou-steps">
                    {st.steps.map((x) => (
                      <div className="ou-steprow" key={`${x.workflowType}:${x.key}`}>
                        <span className={`ou-dot ${x.status === 'done' ? 'paid' : x.status === 'ready' ? 'part' : ''}`} />
                        <span style={{ flex: 1, minWidth: 0 }}>{x.title}</span>
                        {x.status === 'ready' ? <button className="ot-btn sm" onClick={() => onAction(x)}>{x.action?.label || 'Open'}</button> : <span className="faint" style={{ fontSize: 12 }}>{x.status === 'done' ? 'Done' : 'Waiting'}</span>}
                      </div>
                    ))}
                  </div>
                ) : null}
                {open && (st.key === 'instalments' || (st.state === 'now' && s.overdue > 0)) ? (
                  <div style={{ borderTop: '1px solid var(--ot-line)', marginTop: 12, paddingTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <b style={{ fontSize: 13 }}>Payment plan</b>
                      <button className="ot-btn sm" onClick={onRecord}><UIcon name="plus" size={14} /> Record receipt</button>
                    </div>
                    <PlanList s={s} />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ── receipts ── */
const KIND_TONE: Record<string, string> = { Customer: 'acc', Loan: 'good', GST: 'warn', Legal: 'mute', Other: 'mute', Refund: 'bad' };
type ReceiptFilter = 'all' | 'payment' | 'refund' | 'gst' | 'legal';
const RECEIPT_FILTERS: Array<{ id: ReceiptFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'payment', label: 'Payment' },
  { id: 'refund', label: 'Refund' },
  { id: 'gst', label: 'GST' },
  { id: 'legal', label: 'Legal' },
];
export const filterUnitReceipts = (receipts: ReceiptRow[], filter: ReceiptFilter): ReceiptRow[] => {
  if (filter === 'payment') return receipts.filter((receipt) =>
    (receipt.kind === 'Customer' || receipt.kind === 'Loan') && receipt.amount > receipt.refundedAmount
  );
  if (filter === 'refund') return receipts.filter((receipt) => receipt.kind === 'Refund' || receipt.refundedAmount > 0);
  if (filter === 'gst') return receipts.filter((receipt) => receipt.kind === 'GST');
  if (filter === 'legal') return receipts.filter((receipt) => receipt.kind === 'Legal');
  return receipts;
};
// A payment receipt PDF exists only for money paid toward the unit price (buyer payments and loan disbursements).
const hasReceiptPdf = (kind: string) => kind === 'Customer' || kind === 'Loan' || kind === 'GST';

export function ReceiptsCard({ s, onRecord, onReceipts, onDownload }: { s: UnitSummary; onRecord: () => void; onReceipts?: () => void; onDownload?: (txnId: string) => void; compact?: boolean }) {
  // Opens on Payment receipts; falls back to All when the unit has none.
  const [picked, setFilter] = useState<ReceiptFilter>('payment');
  const filter = picked === 'payment' && !filterUnitReceipts(s.receipts, 'payment').length ? 'all' : picked;
  const [busy, setBusy] = useState('');
  // Same as the booking drawer: tapping a receipt opens its payment detail.
  const { openPaymentDetail } = usePaymentDetail();
  const net = (rows: typeof s.receipts) => rows.reduce((sum, r) => sum + (r.kind === 'Refund' ? -r.amount : r.amount), 0);
  const active = RECEIPT_FILTERS.find((f) => f.id === filter)!;
  const rows = filterUnitReceipts(s.receipts, filter);
  const refundedTotal = rows.filter((receipt) => receipt.kind === 'Refund').reduce((sum, receipt) => sum + receipt.amount, 0);
  const visibleAmount = (receipt: ReceiptRow) => filter === 'payment' ? receipt.amount - receipt.refundedAmount : receipt.amount;
  const summaryAmount = filter === 'refund' ? refundedTotal : filter === 'payment'
    ? rows.reduce((sum, receipt) => sum + visibleAmount(receipt), 0)
    : net(rows);
  const download = (id: string) => {
    if (!onDownload) return;
    setBusy(id);
    onDownload(id);
    // The PDF is made by the booking drawer; clear the spinner after a moment.
    window.setTimeout(() => setBusy((b) => (b === id ? '' : b)), 6000);
  };
  const dl = (r: (typeof s.receipts)[number], small?: boolean) =>
    onDownload && hasReceiptPdf(r.kind) ? (
      <button className={small ? 'ou-dl' : 'ot-btn sm'} onClick={(e) => { e.stopPropagation(); download(r.id); }} disabled={busy === r.id} aria-label={`Download receipt for ${rupees(r.amount)} on ${dateLabel(new Date(r.ms))}`} title="Download payment receipt">
        <UIcon name="download" size={14} />{small ? null : busy === r.id ? ' Preparing…' : ' Receipt'}
      </button>
    ) : null;
  return (
    <Card className="ou-wide" index={4} label="Receipts">
      <div className="ot-hd">
        <div><h3>Receipts</h3><p>{rows.length} entr{rows.length === 1 ? 'y' : 'ies'} · {rupees(summaryAmount)}{filter === 'all' ? '' : filter === 'refund' ? ' refunded' : ` ${active.label.toLowerCase()}`}</p></div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onReceipts && s.receipts.some((r) => hasReceiptPdf(r.kind)) ? <button className="ot-btn sm" onClick={onReceipts} title="Download several receipts at once">All receipts</button> : null}
          <button className="ot-btn sm dark" onClick={onRecord}><UIcon name="plus" size={14} /> Record</button>
        </div>
      </div>
      <div className="ou-rchips" role="group" aria-label="Filter receipts">
        {RECEIPT_FILTERS.map((f) => {
          const n = filterUnitReceipts(s.receipts, f.id).length;
          if (f.id !== 'all' && !n) return null;
          return <button key={f.id} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}<b>{n}</b></button>;
        })}
      </div>
      {!rows.length ? <p className="faint" style={{ margin: 0, fontSize: 13 }}>No receipts of this kind.</p> : (
        <div className={`ou-rlist${rows.length > 8 ? ' ou-cap rcap' : ''}`}>
          {rows.map((r) => (
            <div className="ou-rrow" key={r.id} role="button" tabIndex={0} title="Open payment detail" onClick={() => openPaymentDetail(r.id)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPaymentDetail(r.id); } }}>
              <span className="t num">{r.kind === 'Refund' ? '-' : ''}{rupees(visibleAmount(r))} {chip(KIND_TONE[r.kind], r.kind)}{dl(r, true)}</span>
              <span className="d">{dateLabel(new Date(r.ms))}</span>
              <span className="s">{[r.label, r.mode, r.reference, r.refundedAmount > 0 ? `${r.refundedAmount >= r.amount ? 'Fully refunded' : 'Partly refunded'} ${rupees(r.refundedAmount)}` : ''].filter(Boolean).join(' · ')}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ── GST and legal charges ── */
export function ChargesCard({ s, onManage }: { s: UnitSummary; onManage?: () => void }) {
  const row = (label: string, o: { due: number; paid: number }) => (
    <div className="ot-kv" key={label}>
      <span className="muted">{label}</span>
      {o.due <= 0 ? chip('mute', 'Not set') : o.paid >= o.due ? chip('good', `Paid ${rupees(o.paid)}`) : o.paid > 0 ? chip('warn', `${rupees(o.paid)} of ${rupees(o.due)}`) : chip('mute', `${rupees(o.due)} due`)}
    </div>
  );
  return (
    <Card index={5} label="GST and legal charges">
      <div className="ot-hd"><div><h3>GST and legal charges</h3></div>{onManage ? <button className="ot-btn sm" onClick={onManage}>Details</button> : null}</div>
      {row('GST', s.gst)}
      {row('Stamp duty', s.legal.stamp_duty)}
      {row('Registration', s.legal.registration_charges)}
      {row('Advocate agreement fee', s.legal.agreement_fee)}
      {row('Advocate deed fee', s.legal.deed_fee)}
    </Card>
  );
}

/* ── loan ── */
export function LoanCard({ s, onManage, onStart, onStop, onEditSanction }: { s: UnitSummary; onManage?: () => void; onStart?: () => void; onStop?: () => void; onEditSanction?: () => void }) {
  const l = s.loan;
  const [busy, setBusy] = useState<'' | 'start' | 'stop'>('');
  const [confirmStop, setConfirmStop] = useState(false);
  // Clear the waiting state once the change shows up in the data.
  useEffect(() => setBusy(''), [l.needed, l.started]);
  const run = (kind: 'start' | 'stop', fn?: () => void) => {
    if (!fn) return;
    setBusy(kind);
    setConfirmStop(false);
    fn();
    window.setTimeout(() => setBusy((b) => (b === kind ? '' : b)), 8000);
  };
  const canStart = !!onStart && l.hasWorkflow && !l.started;
  // Undo only while no loan step is done (and no money has come from the bank).
  const canStop = !!onStop && l.asked === 'yes' && l.doneSteps === 0 && l.disbursed === 0;
  const sub = !l.needed
    ? 'Buyer is not taking a loan'
    : l.sanctioned
      ? `${l.bank || 'Bank'}${l.sanctionDate ? ` · sanctioned ${l.sanctionDate}` : ''}`
      : l.started
        ? `In progress${l.bank ? ` with ${l.bank}` : ''}`
        : 'Loan needed, file not started';
  return (
    <Card index={6} label="Home loan">
      <div className="ot-hd">
        <div><h3>Home loan</h3><p>{sub}</p></div>
        {l.started && (onManage || onEditSanction) ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {l.sanctioned && onEditSanction ? (
              <button className="ou-ibtn" onClick={onEditSanction} aria-label="Edit sanction details and letter" title="Edit sanction details and letter">
                <svg className="ot-ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
              </button>
            ) : null}
            {onManage ? <button className="ot-btn sm" onClick={onManage}>Loan file</button> : null}
          </div>
        ) : <span className={`ot-ibox c-${l.sanctioned ? 'good' : l.needed ? 'warn' : 'mute'}`}><UIcon name="bank" /></span>}
      </div>
      {l.needed && (l.started || l.amount > 0 || l.disbursed > 0) ? (
        <>
          <div className="ot-statrow">
            <div><div className="ot-lbl">{l.sanctioned ? 'Sanctioned' : 'Loan amount'}</div><div className="ot-mid num">{l.amount ? rupees(l.amount) : '-'}</div></div>
            <div><div className="ot-lbl">Disbursed</div><div className="ot-mid num">{rupees(l.disbursed)}</div></div>
          </div>
          <div className="ot-meter" style={{ margin: '12px 0 0' }}><i className="m-good" style={{ width: `${l.amount ? Math.min(100, (l.disbursed / l.amount) * 100) : 0}%` }} /></div>
        </>
      ) : null}
      {canStart || canStop ? (
        <div className="ou-loanact">
          {canStart ? (
            <button className="ot-btn sm dark" onClick={() => run('start', onStart)} disabled={!!busy}>
              {busy === 'start' ? 'Starting…' : l.needed ? 'Start loan file' : 'Buyer needs a loan · Start loan file'}
            </button>
          ) : null}
          {canStop ? (
            confirmStop ? (
              <span className="ou-loanconfirm">
                Mark as no loan{l.started ? ' and close the empty loan file' : ''}?
                <button className="ou-linkbtn danger" onClick={() => run('stop', onStop)} disabled={!!busy}>{busy === 'stop' ? 'Saving…' : 'Yes, no loan'}</button>
                <button className="ou-linkbtn" onClick={() => setConfirmStop(false)}>Keep</button>
              </span>
            ) : (
              <button className="ou-linkbtn" onClick={() => setConfirmStop(true)} disabled={!!busy}>Buyer doesn't need a loan</button>
            )
          ) : null}
        </div>
      ) : null}
      {!l.hasWorkflow && onStart ? <p className="faint" style={{ margin: '10px 0 0', fontSize: 12.5 }}>Start the booking workflow first (open Full booking view once), then the loan file can start.</p> : null}
    </Card>
  );
}

/* ── buyers ── */
export function BuyersCard({ s, onEdit }: { s: UnitSummary; onEdit: () => void }) {
  return (
    <Card index={7} label="Buyers">
      <div className="ot-hd"><div><h3>Buyers</h3>{s.buyers.length ? null : <p>No buyer added yet</p>}</div><button className="ot-btn sm" onClick={onEdit}>Edit</button></div>
      <div className="ot-list">
        {s.buyers.map((b) => (
          <div className="ot-item" key={b.id}>
            <span className="ou-avatar">{b.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?'}</span>
            <span className="bd">
              <span className="t" style={{ display: 'block' }}>{b.name || 'Name not saved'}</span>
              <span className="s" style={{ display: 'block' }}>{b.role} · {b.kycCount ? `${b.kycCount} KYC document${b.kycCount === 1 ? '' : 's'}` : 'No KYC yet'}</span>
              {b.mobile ? <span className="s mono" style={{ display: 'block', userSelect: 'all' }}>{b.mobile}</span> : null}
            </span>
            {b.mobile ? <a className="ou-ibtn" href={`tel:${b.mobile.replace(/\s+/g, '')}`} aria-label={`Call ${b.name}`}><UIcon name="phone" /></a> : null}
          </div>
        ))}
      </div>
      {s.broker ? <div className="ot-kv" style={{ borderTop: '1px solid var(--ot-line)', marginTop: 6 }}><span className="muted">Broker</span><span>{s.broker}</span></div> : null}
    </Card>
  );
}

export type { DrawerActionType };
